// src/pages/income/_shared/IncomeDraftList.jsx
// ✅ 지출 쪽 _shared/ExpenseDraftList.jsx와 동일한 목적/스타일. CSS는 새로 만들지 않고
// 그 파일이 쓰는 모듈을 그대로 재사용한다(기안서 관리 스타일 재사용 방침을 수입 쪽도
// 동일하게 따름 — 지출목록과 수입목록이 다른 톤일 이유가 없다).
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../api";
import { useTabActivated } from "../../../tabs/TabActivation";
import IncomeDraftDetailModal from "./IncomeDraftDetailModal";
import styles from "../../expense/_shared/ExpenseDraftList.module.css";

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "전체" },
  { value: "draft", label: "작성중" },
  { value: "pending", label: "결재중" },
  { value: "approved", label: "결재완료" },
  { value: "rejected", label: "반려" },
  { value: "none", label: "기안없음" },
];

function statusBadgeInfo(row) {
  if (!row.draft_document_id) return { text: "기안없음", cls: styles.badgeNone };
  const map = {
    draft: { text: "작성중", cls: styles.badgeNone },
    pending: { text: "결재중", cls: styles.badgePending },
    approved: { text: "결재완료", cls: styles.badgeApproved },
    rejected: { text: "반려", cls: styles.badgeRejected },
  };
  return map[row.draft_status] || { text: row.draft_status || "-", cls: styles.badgeNone };
}

export default function IncomeDraftList({ config }) {
  const { dataApiBase, newRoutePath, businessLabel, tabTitle, listRefreshChannelName } = config;
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  useEffect(() => { document.title = `수입목록 - ${tabTitle}`; }, [tabTitle]);

  const [fromDate, setFromDate] = useState(`${year}-01-01`);
  const [toDate, setToDate] = useState(`${year}-12-31`);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [detailRow, setDetailRow] = useState(null);

  const loadList = async (searchVal) => {
    setLoading(true);
    try {
      const { data } = await api.get(dataApiBase, {
        params: { search: searchVal || undefined, ordering: "-draft_date,-seq" },
      });
      setRows(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      console.error("❌ 목록 불러오기 실패:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadList(search); }, [search]);
  useTabActivated(() => loadList(search));

  useEffect(() => {
    if (!("BroadcastChannel" in window)) return;
    const channel = new BroadcastChannel(listRefreshChannelName);
    channel.onmessage = (e) => {
      if (e.data?.type === "refresh") loadList(search);
    };
    return () => channel.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, listRefreshChannelName]);

  const inRange = useMemo(() => {
    const from = new Date(fromDate);
    const to = new Date(toDate);
    to.setHours(23, 59, 59, 999);
    return rows.filter((r) => {
      const d = new Date(r.draft_date);
      return d >= from && d <= to;
    });
  }, [rows, fromDate, toDate]);

  const displayRows = useMemo(() => {
    if (statusFilter === "all") return inRange;
    if (statusFilter === "none") return inRange.filter((r) => !r.draft_document_id);
    return inRange.filter((r) => r.draft_document_id && r.draft_status === statusFilter);
  }, [inRange, statusFilter]);

  const sums = useMemo(() => {
    let total = 0;
    inRange.forEach((r) => { total += Number(r.amount) || 0; });
    return { total };
  }, [inRange]);

  async function handleDelete(row) {
    const hasDraft = !!row.draft_document_id;
    if (hasDraft && row.draft_status === "approved") {
      alert("결재완료 건은 삭제할 수 없습니다. (정책 확정 전까지 비활성화)");
      return;
    }
    const msg = hasDraft
      ? `기안(${row.draft_no})과 연결된 수입 데이터를 함께 삭제합니다.\n\n계속하시겠습니까?`
      : `삭제하시겠습니까?\n\n기안번호: ${row.draft_no}\n\n삭제 후 복구할 수 없습니다.`;
    if (!window.confirm(msg)) return;

    setDeletingId(row.id);
    try {
      if (hasDraft) {
        await api.delete(`/draft/documents/${row.draft_document_id}/`);
      } else {
        await api.delete(`${dataApiBase}${row.id}/`);
      }
      await loadList(search);
    } catch (e) {
      console.error(e);
      const msg2 = e.response?.data ? JSON.stringify(e.response.data).slice(0, 200) : e.message;
      alert(`삭제 실패: ${msg2}`);
    } finally {
      setDeletingId(null);
    }
  }

  if (loading) return <div className={styles.loading}>불러오는 중…</div>;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>💰 {businessLabel} 수입목록</h2>
        <button className={styles.createBtn} onClick={() => navigate(newRoutePath)}>
          ➕ 수입데이터+기안 작성
        </button>
      </div>

      <div className={styles.filters}>
        <div className={styles.filterRow}>
          <span className={styles.filterLabel}>기간</span>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={styles.filterDate} />
          <span className={styles.filterLabel}>~</span>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={styles.filterDate} />

          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={styles.filterSelect}>
            {STATUS_FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <input
            placeholder="기안번호/거래처/제목 검색"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && setSearch(searchInput.trim())}
            className={styles.filterSearch}
          />
          <button onClick={() => setSearch(searchInput.trim())} className={styles.searchBtn}>검색</button>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>작성일</th><th>기안번호</th><th>거래처</th><th>제목</th>
              <th>금액</th><th>기안상태</th><th>관리</th>
            </tr>
          </thead>
          <tbody>
            <tr className={styles.sumRow}>
              <td colSpan={4}>합계(기간 전체, 결재상태 필터 무관)</td>
              <td className={styles.amount}>{sums.total.toLocaleString()}</td>
              <td colSpan={2}></td>
            </tr>
            {displayRows.map((r) => {
              const badge = statusBadgeInfo(r);
              const approved = r.draft_document_id && r.draft_status === "approved";
              return (
                <tr key={r.id}>
                  <td>{r.draft_date}</td>
                  <td className={styles.draftNo}>{r.draft_no}</td>
                  <td className={styles.vendor}>{r.partner?.name ?? r.partner_name ?? "-"}</td>
                  <td className={styles.title}>{r.title}</td>
                  <td className={styles.amount}>{Number(r.amount || 0).toLocaleString()}</td>
                  <td>
                    <span className={`${styles.badge} ${badge.cls}`}>{badge.text}</span>
                  </td>
                  <td>
                    <div className={styles.actionGroup}>
                      <button className={styles.viewBtn} onClick={() => setDetailRow(r)} title="상세보기">🔍</button>
                      <button
                        className={`${styles.deleteBtn} ${approved ? styles.disabledBtn : ""}`}
                        onClick={() => handleDelete(r)}
                        disabled={deletingId === r.id || approved}
                        title={approved ? "결재완료 건은 삭제할 수 없습니다" : "삭제"}
                      >
                        {deletingId === r.id ? "⏳" : "🗑️"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detailRow && (
        <IncomeDraftDetailModal
          config={config}
          row={detailRow}
          onClose={() => setDetailRow(null)}
          onDeleted={() => loadList(search)}
        />
      )}
    </div>
  );
}
